import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { CACHE_TAG_ACCOUNT_DETAILS_BY_ID } from 'api/cacheTagTypes'
import { useLazyGetAccountContainersQuery } from 'api/inventory/accountContainer'
import { useAddUpdateLocationMutation } from 'api/location/addUpdateLocation'
import { useGetGeoZoneLocationExclusionsQuery } from 'api/location/getGeoZoneLocationExclusions'
import CancelButton from 'components/buttons/CancelButton'
import DragPinMessage from 'components/common/address/DragPinMessage'
import { ZONE_FIELD_NAMES } from 'components/common/geo-zone-location/geoZoneLocationExclusion'
import GeoZoneLocationFields from 'components/common/geo-zone-location/GeoZoneLocationFields'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { HHFormTextField, HHFormMuiSwitch, HHFormPriceField } from 'components/form-fields/v5'
import { getAccountMeta } from 'data/meta/accountMetaSelectors'
import { selectIsNewTaxesEnabled, selectIsRouteServiceTimeWindowsEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import { isCustomerDetailsPage } from 'router/routes'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import MapViewV2 from '../MapViewV2'

import AddressForm from './AddressForm'
import LocationContainersList from './LocationContainersList'
import LocationServiceTimes from './LocationServiceTimes'
import { INITIAL_STATE } from './model'
import MoveContainerPinsCard from './MoveContainerPinsCard'

const AddEditLocationDialog = ({ isOpen = false, location, accountId, onClose }) => {
  const customerMeta = useSelector(getAccountMeta, shallowEqual)
  const dispatch = useDispatch()
  const [addUpdateLocation, { isLoading: isAddUpdateLocationLoading }] = useAddUpdateLocationMutation()
  const [getAccountContainers, { currentData: accountContainersData }] = useLazyGetAccountContainersQuery()
  const isNewTaxesEnabled = useSelector(selectIsNewTaxesEnabled)
  const metaTags = get(customerMeta, 'tags', [])
  const locationId = get(location, 'id')
  const isServiceTimeCaptureEnabled = useSelector(selectIsRouteServiceTimeWindowsEnabled)
  // currentData, not data: data keeps the previous location's zones while this one's load.
  const { currentData: savedZones } = useGetGeoZoneLocationExclusionsQuery(locationId, {
    skip: !isOpen || !locationId || !isNewTaxesEnabled,
  })

  const initialState = location || INITIAL_STATE
  const { latitude: initialLatitude, longitude: initialLongitude } = initialState.address
  const [latLngForMap, setLatLngForMap] = useState({
    latitudeForMap: initialLatitude,
    longitudeForMap: initialLongitude,
  })
  const [latLngForMapHistory, setLatLngForMapHistory] = useState({
    latitudeForMapHistory: initialLatitude,
    longitudeForMapHistory: initialLongitude,
  })
  const { latitudeForMap, longitudeForMap } = latLngForMap
  const { latitudeForMapHistory, longitudeForMapHistory } = latLngForMapHistory

  const locationForm = useForm({ defaultValues: INITIAL_STATE })
  const { handleSubmit, control, watch, setValue, resetField, reset, setFocus } = locationForm
  const watchAddress = watch('address')

  const [moveContainerPins, setMoveContainerPins] = useState(true)
  // The endpoint filters by locationId server-side; guard on locationId so add-mode never shows stale data
  const locationContainers = useMemo(
    () => (locationId ? get(accountContainersData, 'containers', []) : []),
    [accountContainersData, locationId]
  )
  const isGeoChanged =
    Number(get(watchAddress, 'latitude')) !== Number(initialLatitude) || Number(get(watchAddress, 'longitude')) !== Number(initialLongitude)
  const hasLocationContainers = locationContainers.length > 0

  const onSubmitHandler = data => {
    // Unsent zones leave the saved exclusions alone; an edit's empty lists before they load would clear them.
    const sendZones = isNewTaxesEnabled && (!locationId || Boolean(savedZones))
    const locationPayload = {
      accountId,
      location: {
        ...omit(data, sendZones ? ['maxPoAmount'] : ['maxPoAmount', 'zones']),
        // New API accept maxPoAmount but old API accept maxPOAmount
        maxPOAmount: get(data, 'maxPoAmount'),
        updateContainerLocation: hasLocationContainers && isGeoChanged && moveContainerPins,
      },
    }

    addUpdateLocation(locationPayload)
      .unwrap()
      .then(() => {
        onClose()
        if (isCustomerDetailsPage()) {
          dispatch(getCustomerDetails({ accountId }))
        }
        dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId }]))
        toast.success(location ? 'Location updated successfully' : 'Location added successfully')
      })
      .catch(handleError)
  }

  const onSubmitErrorHandler = () => {
    // eslint-disable-next-line no-restricted-syntax -- a form validation message, not a request error
    toast.error(T.REQUIRED_FIELD_MSG)
  }

  const handleLatLongFromMapView = useCallback(
    (long, lat) => {
      setValue('address.longitude', long)
      setValue('address.latitude', lat)
      setLatLngForMap({ longitudeForMap: long, latitudeForMap: lat })
    },
    [setValue]
  )

  const handleMapChange = useCallback(({ mapLng, mapLat }) => handleLatLongFromMapView(mapLng, mapLat), [handleLatLongFromMapView])

  const handleMapReset = useCallback(
    () => handleLatLongFromMapView(longitudeForMapHistory, latitudeForMapHistory),
    [handleLatLongFromMapView, longitudeForMapHistory, latitudeForMapHistory]
  )

  const handleLocationPurchaseOrderChange = useCallback(
    checked => {
      if (!checked) {
        resetField('po')
        resetField('maxPoAmount')
        return
      }

      setTimeout(() => setFocus('po'), 100)
    },
    [resetField, setFocus]
  )

  useEffect(() => {
    if (isOpen) {
      if (location) {
        // A location the backend serves without zones must still reset onto the documented default.
        reset({ ...INITIAL_STATE, ...location }, { keepDefaultValues: true })
      } else {
        reset(INITIAL_STATE)
      }
      setLatLngForMapHistory({ longitudeForMapHistory: initialLongitude, latitudeForMapHistory: initialLatitude })
      handleLatLongFromMapView(initialLongitude, initialLatitude)
      setMoveContainerPins(true)
      if (locationId) {
        getAccountContainers({ accountId, locationId })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location])

  // Re-applied after every open reset, per field path: a zone field does not hear a set on the parent `zones`.
  useEffect(() => {
    if (isOpen && savedZones) {
      setValue(ZONE_FIELD_NAMES.pricing, savedZones.excludedPricingZoneIds)
      setValue(ZONE_FIELD_NAMES.service, savedZones.excludedServiceZoneIds)
    }
  }, [isOpen, location, savedZones, setValue])

  return (
    <>
      <HHBaseDialog disableScrollLock fullWidth maxWidth="lg" open={isOpen} onClose={onClose}>
        <HHDialogTitle title={location ? 'Edit location' : 'Add location'} onClose={onClose} />
        <DialogContent>
          <Grid container columnGap={3} mt={2}>
            <Grid
              container
              size={{
                xs: 12,
                md: 'grow',
              }}
              spacing={2}
            >
              <Grid minHeight={480} size={12}>
                <MapViewV2
                  containers={locationContainers}
                  latitude={latitudeForMap}
                  latitudeForMapHistory={latitudeForMapHistory}
                  longitude={longitudeForMap}
                  longitudeForMapHistory={longitudeForMapHistory}
                  previewContainersAtPin={hasLocationContainers && isGeoChanged && moveContainerPins}
                  onChange={handleMapChange}
                  onReset={handleMapReset}
                />
              </Grid>
              <GeoZoneLocationFields control={control} latitude={watchAddress.latitude} longitude={watchAddress.longitude} />
              {hasLocationContainers ? (
                <Grid size={12}>
                  <MoveContainerPinsCard checked={moveContainerPins} containers={locationContainers} onChange={setMoveContainerPins} />
                </Grid>
              ) : (
                <DragPinMessage latitude={latitudeForMap} longitude={longitudeForMap} />
              )}
            </Grid>

            <Grid
              container
              height="fit-content"
              rowGap={2}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <FormProvider {...locationForm}>
                <AddressForm metaTags={metaTags} setLatLngForMap={setLatLngForMap} setLatLngForMapHistory={setLatLngForMapHistory} />
              </FormProvider>
              {/* The optimizer treats these hours as each stop's default window. */}
              {isServiceTimeCaptureEnabled && (
                <Grid size={12}>
                  <LocationServiceTimes control={control} />
                </Grid>
              )}
              <Grid size={12}>
                <HHFormMuiSwitch control={control} label="Signature required" name="signatureRequired" />
              </Grid>
              <Grid size={12}>
                <HHFormMuiSwitch
                  control={control}
                  label="Location purchase order"
                  name="locationPurchaseOrder"
                  onChange={handleLocationPurchaseOrderChange}
                />
              </Grid>
              {watch('locationPurchaseOrder') && (
                <>
                  <Grid size={6}>
                    <HHFormTextField
                      fullWidth
                      control={control}
                      deprecatedLabel={false}
                      label={T.PO_NUMBER_PLACEHOLDER}
                      name="po"
                      placeholder={T.PO_NUMBER_PLACEHOLDER}
                    />
                  </Grid>
                  <Grid size={6}>
                    <HHFormPriceField
                      dollarPrefix
                      fullWidth
                      control={control}
                      deprecatedLabel={false}
                      label="Max PO amount"
                      name="maxPoAmount"
                      placeholder="Max PO amount"
                    />
                  </Grid>
                </>
              )}
              {hasLocationContainers && (
                <Grid size={12}>
                  <LocationContainersList containers={locationContainers} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <HHDialogActions>
          <CancelButton onClick={onClose} />
          <Button
            loading={isAddUpdateLocationLoading}
            size="small"
            variant="contained"
            onClick={handleSubmit(onSubmitHandler, onSubmitErrorHandler)}
          >
            {T.SAVE}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
    </>
  )
}

AddEditLocationDialog.propTypes = {
  isOpen: PropTypes.bool,
  location: PropTypes.object,
  accountId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default AddEditLocationDialog
